import { downloadZip } from 'client-zip';
import { ERROR_CODES, ErrCode } from '../../../../shared/models';
import { IZipWriter, ZipFileInput } from '../../../models';

/** Delay before revoking the blob URL to ensure the browser has claimed it */
const REVOKE_DELAY_MS = 10000;

/**
 * Zip writer that feeds files to client-zip via an async generator.
 * Only one file's chunks are held in memory at a time.
 * Chrome disk-backs the resulting Blob for large zips.
 */
export class ZipWriterBufferedService implements IZipWriter {
  private zipName = '';
  private aborted = false;

  private readyFiles: ZipFileInput[] = [];
  private fileReady: { resolve: () => void } | null = null;
  private isFinalized = false;
  private blobPromise: Promise<Blob> | null = null;

  public init(zipName: string): void {
    this.reset();
    this.zipName = zipName;
    this.aborted = false;

    const response = downloadZip(this.fileGenerator());
    this.blobPromise = response.blob();
  }

  public async addFileBlob(relativePath: string, blob: Blob, modDate?: Date): Promise<void> {
    if (this.aborted) {
      return;
    }

    this.readyFiles.push({
      name: relativePath,
      lastModified: modDate || new Date(),
      input: blob,
    });

    if (this.fileReady) {
      this.fileReady.resolve();
      this.fileReady = null;
    }
  }

  public async finalize(): Promise<void> {
    if (this.aborted || !this.blobPromise) {
      throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CREATE_ERROR);
    }

    this.isFinalized = true;
    if (this.fileReady) {
      this.fileReady.resolve();
      this.fileReady = null;
    }

    const blob = await this.blobPromise;

    if (this.aborted) {
      throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CREATE_ERROR);
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.zipName;
    link.click();

    setTimeout((): void => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
    this.reset();
  }

  public async abort(): Promise<void> {
    this.aborted = true;
    if (this.fileReady) {
      this.fileReady.resolve();
      this.fileReady = null;
    }
    this.reset();
  }

  /**
   * Yields files one at a time to client-zip.
   * Blocks until each file is finalized via the fileReady promise.
   */
  private async *fileGenerator(): AsyncGenerator<ZipFileInput> {
    while (!this.aborted) {
      if (this.readyFiles.length === 0) {
        if (this.isFinalized) {
          return;
        }
        await new Promise<void>((resolve): void => {
          this.fileReady = { resolve };
        });
      }

      if (this.aborted) {
        return;
      }

      while (this.readyFiles.length > 0) {
        yield this.readyFiles.shift();
      }

      if (this.isFinalized && this.readyFiles.length === 0) {
        return;
      }
    }
  }

  private reset(): void {
    this.readyFiles = [];
    this.zipName = '';
    this.fileReady = null;
    this.isFinalized = false;
    this.blobPromise = null;
  }
}
